import React, { useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Bar,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { formatCurrency } from '../lib/utils';
import { formatCompact, formatPercent } from '../lib/chartFormatters';

// Sales by closing date (Fan, 2026-10-24): "the tab by sales closing date,
// which helps me keep track of our sales performance". Sold is on Aria's
// basis (Fan, 2026-10-25): each deal whole in its closing month, live rolling
// bookings projected to 12 months from today. Data comes from
// GET /reports/bros/sales (services/bros_sales_by_close.py). GBP from the
// API; cx() converts for the EUR toggle like every other BR OS tab.

const KIND_LABEL = { booking: 'Booking', rolling_carry: 'Rolling, closed earlier', cancellation: 'Cancellation' };

function SalesKpi(props) {
  const { label, value, sub } = props;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold font-mono">{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function SalesTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  return (
    <div className="rounded-md border bg-background p-3 text-xs shadow-md space-y-0.5">
      <p className="font-semibold text-sm mb-1">{label}</p>
      <p style={{ color: '#3B82F6' }}>Sold: {formatCurrency(row.Sold || 0, currency)}</p>
      <p className="text-muted-foreground pl-2">Booked ({row._deals} deals): {formatCurrency(row._booked || 0, currency)}</p>
      {row._rolling > 0 && (
        <p className="text-muted-foreground pl-2">Rolling, next 12 months ({row._rolling}): {formatCurrency(row._rollingValue || 0, currency)}</p>
      )}
      {row._canc > 0 && (
        <p className="text-muted-foreground pl-2">Cancellation fees ({row._canc}): {formatCurrency(row._cancValue || 0, currency)}</p>
      )}
      <p style={{ color: '#F59E0B' }}>Profit: {formatCurrency(row.Profit || 0, currency)}</p>
      {row.Margin != null && <p style={{ color: '#10B981' }}>Margin: {formatPercent(row.Margin, 1)}</p>}
      <p style={{ color: '#06B6D4' }}>Deals closed: {row.Deals}</p>
    </div>
  );
}

export function salesChartRows(months, cx) {
  return (months || []).map((m) => ({
    month: (m.label || '').slice(0, 3),
    monthKey: m.month,
    Sold: cx(parseFloat(m.sales || 0)),
    Profit: cx(parseFloat(m.margin || 0)),
    Margin: m.margin_pct,
    Deals: m.deals || 0,
    _deals: m.deals || 0,
    _booked: cx(parseFloat(m.booked_value || 0)),
    _rolling: m.rolling_deals || 0,
    _rollingValue: cx(parseFloat(m.rolling_value || 0)),
    _canc: m.cancellations || 0,
    _cancValue: cx(parseFloat(m.cancellation_fees || 0)),
  }));
}

export function BROSDashboardSalesTab(props) {
  const { data, loading, dc, cx } = props;
  const [openMonth, setOpenMonth] = useState(null);

  const rows = useMemo(() => salesChartRows(data?.months, cx), [data, cx]);
  const maxDeals = useMemo(() => Math.max(1, ...rows.map((r) => r.Deals || 0)), [rows]);

  if (!data && !loading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No data available.</p>;
  }
  if (!data) return null;

  const k = data.kpis || {};
  const legacyEst = data.warnings?.legacy_estimated || 0;

  return (
    <div className="space-y-6" data-testid="bros-sales-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SalesKpi
          label={`Sold (${dc})`}
          value={formatCurrency(cx(k.sales || 0), dc)}
          sub={`${formatCurrency(cx(k.booked_value || 0), dc)} booked + ${formatCurrency(cx(k.rolling_value || 0), dc)} rolling${k.cancellations ? ` + ${k.cancellations} cancellation fees` : ''}`}
        />
        <SalesKpi
          label={`Profit (${dc})`}
          value={formatCurrency(cx(k.margin || 0), dc)}
          sub="Sold less supplier cost"
        />
        <SalesKpi
          label="Margin %"
          value={k.margin_pct != null ? formatPercent(k.margin_pct, 1) : '-'}
          sub="Profit / sold"
        />
        <SalesKpi
          label="Deals closed"
          value={String(k.deals || 0)}
          sub={`${k.rolling_deals || 0} rolling, avg ${formatCurrency(cx(k.avg_deal_value || 0), dc)} booked`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sales by closing date</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="sales-basis">
            Same basis as Aria's revenue tracking. Each deal counts once, whole, in the month it was <strong>closed</strong> (confirmed in BR OS); extensions stay with the original deal. <strong>Rolling</strong> bookings that are still live are projected to 12 months from today at their own nightly rate, and that projection sits in the closing month (or the first month shown, if the deal closed earlier). HubSpot imports use the HubSpot close date. Value = contract value ex VAT. Cancelled bookings count only their cancellation fee. This tracks sales, not revenue: the P&L tab dates revenue by invoice due date.
          </p>
          {legacyEst > 0 && (
            <p className="text-xs text-amber-700 mt-1" data-testid="sales-legacy-note">
              {legacyEst} HubSpot imports have no won deal in HubSpot, so they are dated by check-in.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={rows} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
              <YAxis yAxisId="money" width={50} stroke="hsl(var(--muted-foreground))" fontSize={12}
                tickFormatter={(v) => formatCompact(v, dc)} />
              <YAxis yAxisId="pct" orientation="right" width={40} stroke="#10B981" fontSize={12}
                tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
              <YAxis yAxisId="deals" orientation="right" width={35} stroke="#06B6D4" fontSize={12}
                domain={[0, Math.ceil(maxDeals * 1.3)]} tickFormatter={(v) => String(Math.round(v))} />
              <Tooltip content={<SalesTooltip currency={dc} />} />
              <Legend />
              <Bar dataKey="Sold" yAxisId="money" fill="#3B82F6" barSize={28}>
                <LabelList dataKey="Sold" position="top"
                  content={({ x, y, width, value }) => (value ? (
                    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fontWeight={600} fill="#3B82F6">
                      {formatCompact(value, dc)}
                    </text>
                  ) : null)} />
              </Bar>
              <Bar dataKey="Profit" yAxisId="money" fill="#F59E0B" barSize={28} />
              <Line dataKey="Deals" name="Deals closed" yAxisId="deals" type="monotone" stroke="#06B6D4" strokeWidth={2}
                dot={{ r: 4, fill: '#06B6D4', stroke: '#fff', strokeWidth: 2 }} />
              <Line dataKey="Margin" yAxisId="pct" type="monotone" stroke="#10B981" strokeWidth={2}
                dot={{ r: 3, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Month by month</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Click a month to see the deals closed in it.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Sold</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead className="text-right">Deals</TableHead>
                <TableHead className="text-right">Of which rolling</TableHead>
                <TableHead className="text-right">Cancellation fees</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.months || []).slice().reverse().map((m) => {
                const open = openMonth === m.month;
                return (
                  <Fragment key={m.month}>
                    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setOpenMonth(open ? null : m.month)}>
                      <TableCell className="font-medium">
                        {open ? <ChevronDown className="inline h-3 w-3 mr-1" /> : <ChevronRight className="inline h-3 w-3 mr-1" />}
                        {m.label} {m.month.slice(0, 4)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(m.sales || 0)), dc)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(m.margin || 0)), dc)}</TableCell>
                      <TableCell className="text-right font-mono">{m.margin_pct != null ? formatPercent(m.margin_pct, 1) : '-'}</TableCell>
                      <TableCell className="text-right">{m.deals}</TableCell>
                      <TableCell className="text-right font-mono">
                        {m.rolling_deals ? formatCurrency(cx(parseFloat(m.rolling_value || 0)), dc) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {m.cancellations ? formatCurrency(cx(parseFloat(m.cancellation_fees || 0)), dc) : '-'}
                      </TableCell>
                    </TableRow>
                    {open && (m.rows || []).map((r, i) => (
                      <TableRow key={`${m.month}-${r.booking_id}-${r.kind}-${i}`} className="bg-muted/30 text-xs">
                        <TableCell className="pl-8">
                          <span className="font-mono">{r.booking_id}</span>
                          <Badge variant="outline" className="ml-2 text-[10px]">{KIND_LABEL[r.kind] || r.kind}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(r.value || 0)), dc)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(r.margin || 0)), dc)}</TableCell>
                        <TableCell colSpan={4} className="text-muted-foreground">
                          {r.company}
                          {' \u00B7 closed '}{r.closed_on}
                          {r.closed_on_source === 'hubspot' ? ' (HubSpot)' : ''}
                          {r.closed_on_source === 'estimated' ? ' (check-in, HubSpot import)' : ''}
                          {r.check_in ? ` \u00B7 stay ${r.check_in} to ${r.check_out || '?'}` : ''}
                          {r.nights ? ` \u00B7 ${r.nights} nights booked` : ''}
                          {r.rolling ? ` \u00B7 rolling: +${r.projected_nights} nights = ${formatCurrency(cx(parseFloat(r.projected || 0)), dc)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {(data.top_clients || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top clients by sales</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Sold</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="text-right">Deals</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_clients.map((c) => (
                  <TableRow key={c.company}>
                    <TableCell>{c.company}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(c.sales || 0)), dc)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(cx(parseFloat(c.margin || 0)), dc)}</TableCell>
                    <TableCell className="text-right font-mono">{c.margin_pct != null ? formatPercent(c.margin_pct, 1) : '-'}</TableCell>
                    <TableCell className="text-right">{c.bookings}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
